import type { ReactNode } from "react";
import { CloseHealth } from "@common/types/closeHealth";
import { CloseStatus, closeStatusLabels } from "@common/types/closeStatus";
import { DEFAULT_ESTIMATED_TIME_CELL_VARIANT } from "@common/types/views/clientsWorkOverviewView";
import { useCloseTemplates } from "@api/closeTemplate.api";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { DateRangeDisplay } from "@components/settings/OrganizationCloseTemplates/DateRangeDisplay";
import {
    DescriptionList,
    DescriptionListItem,
} from "@design-system/DescriptionList/DescriptionList";
import { EmptyValue } from "@design-system/EmptyValue/EmptyValue";
import { Link } from "@design-system/Link/Link";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import { WorkEstimatedTime } from "../cells/WorkEstimatedTimeCell";
import { WorkHealth } from "../cells/WorkHealthCell";
import { WorkTaskProgress } from "../cells/WorkTaskProgressCell";
import { daysInStage } from "../workRowCompare";
import {
    getWorkFinishedState,
    type WorkFinishedState,
} from "../workStageGroups";
import { getWorkFinishedStatus } from "../board/workBoardStatus";
import { getWorkTeamAndDateFields } from "./WorkEditableFields";

const CLOSE_STATUS_BY_FINISHED_STATE: Record<WorkFinishedState, CloseStatus> = {
    canceled: CloseStatus.CANCELLED,
    done: CloseStatus.DONE,
};

/** Canceled, Done or Open, by the same rule the board's Done and Canceled columns use. */
const getStatus = (row: ClientsWorkOverviewWorkRow) => {
    const finishedState = getWorkFinishedState(row);
    return finishedState
        ? CLOSE_STATUS_BY_FINISHED_STATE[finishedState]
        : CloseStatus.OPEN;
};

const valueOrEmpty = (value: ReactNode) => value || <EmptyValue />;

const WorkTemplateLink = ({ templateId }: { templateId: string | null }) => {
    const { data: templates } = useCloseTemplates({
        enabled: templateId !== null,
        includeArchived: true,
        refetchOnMount: false,
    });
    if (!templateId) {
        return <EmptyValue />;
    }
    const name =
        templates?.find(({ id }) => id === templateId)?.name ?? "View template";
    const params = new URLSearchParams({ templateId });
    return (
        <Link
            render={
                <StateAwareLink
                    to={`/templates/services?${params.toString()}`}
                />
            }
        >
            {name}
        </Link>
    );
};

/** The work item's fields in the spec's order; open work edits its team and dates in place. */
export const WorkDetailsSection = ({
    row,
}: {
    row: ClientsWorkOverviewWorkRow;
}) => {
    const days = daysInStage(row);
    const fields: Array<{ label: string; value: ReactNode }> = [
        { label: "Status", value: closeStatusLabels[getStatus(row)] },
        {
            label: "Stage",
            value:
                getWorkFinishedStatus(row)?.label ??
                valueOrEmpty(row.closeStageName),
        },
        {
            label: "Days in stage",
            value: days === null ? <EmptyValue /> : `${days} d`,
        },
        {
            label: "Progress",
            value:
                row.totalTaskCount > 0 ? (
                    <WorkTaskProgress row={row} variant="count" />
                ) : (
                    <EmptyValue />
                ),
        },
        {
            label: "Est. time",
            value:
                row.estimatedTimeRemaining + row.estimatedTimeDone > 0 ? (
                    <WorkEstimatedTime
                        row={row}
                        variant={DEFAULT_ESTIMATED_TIME_CELL_VARIANT}
                    />
                ) : (
                    <EmptyValue />
                ),
        },
        {
            label: "Health",
            value:
                row.health === CloseHealth.NONE ? (
                    <EmptyValue />
                ) : (
                    <WorkHealth row={row} />
                ),
        },
        ...getWorkTeamAndDateFields(row),
        { label: "Service", value: row.name },
        {
            label: "Service period",
            value:
                row.startDate || row.endDate ? (
                    <DateRangeDisplay
                        startDate={row.startDate}
                        endDate={row.endDate}
                    />
                ) : (
                    <EmptyValue />
                ),
        },
        { label: "Client", value: row.workspaceName },
        { label: "Entity", value: row.entityName },
        {
            label: "Service template",
            value: <WorkTemplateLink templateId={row.closeTemplateId} />,
        },
    ];

    return (
        <DescriptionList>
            {fields.map(({ label, value }) => (
                <DescriptionListItem key={label} label={label} layout="columns">
                    {value}
                </DescriptionListItem>
            ))}
        </DescriptionList>
    );
};
